import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, Area, AreaChart } from "recharts";
import { TrendingUp, Zap, MapPin, Sun, Battery, Sparkles, ChevronRight, TrendingDown, Plug } from "lucide-react";
import { useVehicle } from "../../../lib/vehicleContext";
import { getClimate } from "../../../lib/climateData";
import { VEHICLE_USAGE_HINTS } from "./_shared";
import { WinterRangeTaxCard } from "../WinterRangeTaxCard";
import { LiveTempImpactCard } from "../LiveTempImpactCard";
import { CollapseSection } from "../CollapseSection";
import {
  capacityRetention,
  degradationVelocity,
  futureHealthForecast,
  dcFastChargeSensitivity,
} from "../../../lib/batteryAnalytics";
import {
  PillarSectionHeader,
  MeterBarCard,
  StatChipCard,
  FactorListCard,
  COLOR,
} from "../pillars/_cards";

/**
 * PredictView — Pillar 2, "answers not data".
 *
 * Three modules:
 *   1. 8-year SoH forecast (Recharts area)
 *   2. Digital Twin what-if simulator (5 preset toggles + live re-projection)
 *   3. Fleet-relative aging card (percentile vs cohort)
 */
export const PredictView = ({ stateCode }) => {
  const { vehicle } = useVehicle();
  const climate = useMemo(() => getClimate(stateCode), [stateCode]);
  const hints = VEHICLE_USAGE_HINTS[vehicle.id] || VEHICLE_USAGE_HINTS["model-3-lr"];

  const soh = capacityRetention(vehicle);
  const vel = degradationVelocity(vehicle);
  const forecast = futureHealthForecast(vehicle);
  const dcSens = dcFastChargeSensitivity(vehicle, hints);
  const floor = vehicle.warranty?.threshold ?? 70;

  // Digital Twin — 5 boolean toggles that adjust the projected slope.
  // Persisted per vehicle so selections survive reloads until turned off.
  const twinKey = `eviq.twin_toggles.${vehicle.id}`;
  const [twin, setTwin] = useState(() => {
    const defaults = {
      limit80: false,     // charge to 80% instead of 100%
      reduceDc: false,    // reduce Supercharger by 50%
      homeL1: false,      // 120V trickle charging at home
      homeL2: false,      // shift to home L2 majority
      coolerHome: false,  // move to a cooler climate
      keep5yrs: true,     // hold vehicle 5 years
    };
    try {
      const saved = JSON.parse(localStorage.getItem(twinKey) || "null");
      return saved ? { ...defaults, ...saved } : defaults;
    } catch (e) {
      return defaults;
    }
  });

  useEffect(() => {
    try { localStorage.setItem(twinKey, JSON.stringify(twin)); } catch (e) {}
  }, [twin, twinKey]);

  // Compute an adjusted monthly velocity from twin toggles.
  const adjVel = useMemo(() => {
    let v = vel;
    if (twin.limit80) v *= 0.75;
    if (twin.reduceDc) v *= 0.88;
    if (twin.homeL1) v *= 0.83;
    if (twin.homeL2) v *= 0.86;
    if (twin.coolerHome) v *= 0.78;
    return Math.max(0.02, +v.toFixed(3));
  }, [vel, twin]);

  const projectionSeries = useMemo(() => {
    const months = twin.keep5yrs ? 60 : 96;
    const now = soh;
    const base = [];
    const twinArr = [];
    for (let m = 0; m <= months; m += 3) {
      base.push({ m, soh: Math.max(60, +(now - vel * m).toFixed(1)) });
      twinArr.push({ m, soh: Math.max(60, +(now - adjVel * m).toFixed(1)) });
    }
    return { base, twinArr, months };
  }, [soh, vel, adjVel, twin.keep5yrs]);

  // Combine into one dataset for Recharts.
  const combined = projectionSeries.base.map((p, i) => ({
    m: p.m,
    yrs: +(p.m / 12).toFixed(1),
    base: p.soh,
    twin: projectionSeries.twinArr[i]?.soh ?? p.soh,
  }));

  // Fleet rank vs age-adjusted median (industry baseline 2.4 %/yr).
  const ageYears = Math.max(0.5, new Date().getFullYear() - (vehicle.year || new Date().getFullYear() - 3));
  const expectedSoh = 100 - 2.4 * ageYears;
  const percentile = Math.max(3, Math.min(97, Math.round(50 + (soh - expectedSoh) * 6)));

  return (
    <div className="space-y-4 pb-4" data-testid="mobile-app-view-predict">
      <PillarSectionHeader
        eyebrow="Predictive Intelligence"
        title="What happens next."
        kicker="Every insight below is a projection — grounded in your telemetry and the ~2.4%/yr industry median."
      />

      {/* Fleet-relative aging headline */}
      <div
        className="rounded-2xl border p-4"
        style={{
          borderColor: "rgba(0,229,255,0.28)",
          background: "linear-gradient(135deg, rgba(0,229,255,0.06), rgba(57,255,20,0.04))",
        }}
        data-testid="predict-fleet-rank-hero"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center border"
            style={{
              background: "rgba(0,229,255,0.14)",
              borderColor: "rgba(0,229,255,0.45)",
            }}
          >
            <TrendingUp className="w-4 h-4" style={{ color: COLOR.cyan }} strokeWidth={2.4} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              Fleet aging
            </div>
            <div className="text-sm text-white font-semibold leading-tight mt-0.5">
              {soh >= expectedSoh
                ? `Aging ${(expectedSoh - soh) < -1 ? "slower than" : "in line with"} ~${percentile}% of similar packs.`
                : `Aging faster than expected — top ${100 - percentile}%.`}
            </div>
          </div>
        </div>
      </div>

      {/* 8-year SoH forecast */}
      <CollapseSection id="predict-projection" eyebrow="Projection" title={`Next ${twin.keep5yrs ? "5" : "8"} years`} defaultOpen>
      <div
        className="rounded-xl border p-3"
        style={{ borderColor: COLOR.zinc, background: "rgba(255,255,255,0.02)" }}
        data-testid="predict-forecast-chart"
      >
        <div className="h-48 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={combined} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id="baseArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFB020" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="#FFB020" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="twinArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#39FF14" stopOpacity={0.24} />
                  <stop offset="100%" stopColor="#39FF14" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="yrs"
                tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9, fontFamily: "monospace" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}y`}
              />
              <YAxis
                domain={[60, 100]}
                tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9, fontFamily: "monospace" }}
                axisLine={false}
                tickLine={false}
                width={28}
                tickFormatter={(v) => `${v}%`}
              />
              <ReferenceLine y={floor} stroke="#FF6B57" strokeDasharray="3 3" label={{ value: `Warranty floor ${floor}%`, position: "insideBottomRight", fill: "#FF6B57", fontSize: 9, fontFamily: "monospace" }} />
              <Tooltip
                contentStyle={{ background: "rgba(0,0,0,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11 }}
                labelFormatter={(v) => `Year ${v}`}
                formatter={(v, n) => [`${v}%`, n === "base" ? "Current trajectory" : "With twin toggles"]}
              />
              <Area type="monotone" dataKey="base" stroke="#FFB020" strokeWidth={1.8} fill="url(#baseArea)" />
              <Area type="monotone" dataKey="twin" stroke="#39FF14" strokeWidth={1.8} fill="url(#twinArea)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 mt-2 px-2">
          <LegendSwatch color="#FFB020" label="Current trajectory" />
          <LegendSwatch color="#39FF14" label="With your twin toggles" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatChipCard label="Current velocity" value={`${vel.toFixed(2)}%/mo`} color={COLOR.amber} trend="observed rate" testid="predict-current-velocity" />
        <StatChipCard label="With twin toggles" value={`${adjVel.toFixed(2)}%/mo`} color={COLOR.green} trend={`−${(((vel - adjVel) / vel) * 100).toFixed(0)}% improvement`} testid="predict-adjusted-velocity" />
      </div>
      </CollapseSection>

      {/* Digital Twin what-if simulator */}
      <CollapseSection
        id="predict-whatif"
        eyebrow="Digital Twin"
        title="What if…"
        kicker="Toggle any combination — the projection above updates instantly."
        defaultOpen
      >
      <LiveTempImpactCard vehicle={vehicle} climate={climate} floor={floor} soh={soh} vel={vel} />

      <div className="space-y-2" data-testid="predict-twin-toggles">
        <TwinToggle icon={Battery}  color={COLOR.green} label="Charge to 80% instead of 100%" desc="Reduces calendar aging by ~25%." on={twin.limit80} onChange={() => setTwin(t => ({ ...t, limit80: !t.limit80 }))} testid="twin-limit80" />
        <TwinToggle icon={Zap}      color={COLOR.cyan}  label="Cut DC fast charging by 50%" desc="Reduces cumulative thermal stress." on={twin.reduceDc} onChange={() => setTwin(t => ({ ...t, reduceDc: !t.reduceDc }))} testid="twin-reduce-dc" />
        <TwinToggle icon={Plug}     color={COLOR.cyan}  label="Home L1 (120V) trickle charging" desc="The gentlest daily charge — slow, cool, near-zero pack stress." on={twin.homeL1} onChange={() => setTwin(t => ({ ...t, homeL1: !t.homeL1 }))} testid="twin-home-l1" />
        <TwinToggle icon={Sparkles} color={COLOR.green} label="Home L2 as the majority source" desc="Slow, cool sessions preserve chemistry." on={twin.homeL2} onChange={() => setTwin(t => ({ ...t, homeL2: !t.homeL2 }))} testid="twin-home-l2" />
        <TwinToggle icon={Sun}      color={COLOR.blue}  label="Move to a cooler climate" desc="Ambient temp is the biggest lever for calendar aging." on={twin.coolerHome} onChange={() => setTwin(t => ({ ...t, coolerHome: !t.coolerHome }))} testid="twin-cooler-home" />
        <TwinToggle icon={MapPin}   color={COLOR.amber} label={twin.keep5yrs ? "Holding 5 years" : "Holding 8 years"} desc="Extends the projection horizon." on={twin.keep5yrs} onChange={() => setTwin(t => ({ ...t, keep5yrs: !t.keep5yrs }))} testid="twin-hold" />
      </div>
      </CollapseSection>

      {/* DC-fast-charge sensitivity */}
      <CollapseSection id="predict-sensitivity" eyebrow="Sensitivity" title="Impact of one habit">
      <FactorListCard
        header="If you reduce DC fast charging by 20%"
        testid="predict-dc-sensitivity"
        items={[
          { icon: TrendingDown, color: COLOR.green, text: `Projected 3-year SoH: ${dcSens.projectedNewSoh3y}%`, hint: `+${dcSens.projectedSavingsPct}% vs the current trajectory` },
          { icon: Sparkles,     color: COLOR.cyan,  text: `Current DC share: ${dcSens.currentSuperchargerPct}% of miles`, hint: "Based on your driving profile" },
        ]}
      />
      </CollapseSection>

      {/* Seasonal — what winter costs this exact car in this climate */}
      <CollapseSection id="predict-winter" eyebrow="Seasonal" title="Winter range tax">
      <WinterRangeTaxCard vehicle={vehicle} climate={climate} />
      </CollapseSection>
    </div>
  );
};

const LegendSwatch = ({ color, label }) => (
  <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-zinc-400">
    <span className="w-3 h-0.5" style={{ background: color }} />
    {label}
  </span>
);

const TwinToggle = ({ icon: Icon, color, label, desc, on, onChange, testid }) => (
  <button
    type="button"
    onClick={onChange}
    data-testid={testid}
    className="w-full text-left rounded-xl border px-3 py-3 flex items-center gap-3 hover:bg-white/[0.04] transition-colors"
    style={{
      borderColor: on ? `${color}55` : COLOR.zinc,
      background: on ? `${color}0e` : "rgba(255,255,255,0.02)",
    }}
    aria-pressed={on}
  >
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
      style={{
        background: `${color}14`,
        borderColor: on ? `${color}77` : "rgba(255,255,255,0.10)",
      }}
    >
      <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.2} />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] text-white font-semibold leading-tight">{label}</div>
      <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">{desc}</div>
    </div>
    <div
      className="shrink-0 w-9 h-5 rounded-full relative transition-colors"
      style={{ background: on ? color : "rgba(255,255,255,0.10)" }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-black transition-transform"
        style={{ left: on ? "18px" : "2px" }}
      />
    </div>
  </button>
);
